/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {KTIcon, toAbsoluteUrl} from '../../../../_metronic/helpers'
import {getUserByToken, login} from '../core/_requests'
import {useAuth} from '../core/Auth'

const loginSchema = Yup.object().shape({
  phone_number: Yup.string()
    .matches(/^\+998\d{9}$/, 'Номер телефона должен быть в формате +998XXXXXXXXX')
    .required('Введите номер телефона'),
  password: Yup.string()
    .min(3, 'Минимум 3 символа')
    .max(50, 'Максимум 50 символов')
    .required('Введите пароль'),
})

const initialValues = {
  phone_number: '+998',
  password: '',
}

const inputStyle = {borderRadius: 12}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.phone_number, values.password)
        saveAuth(auth)
        const {data: user} = await getUserByToken(auth.access)
        setCurrentUser(user)
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus('Неверный номер телефона или пароль')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {/* begin::Logo */}
      <div className='d-flex align-items-center justify-content-center text-center gap-3 mb-8'>
        <img src={toAbsoluteUrl('/media/logos/gmd-favicon.svg')} alt='' style={{width: 40, height: 40}} />
        <span className='fw-bolder fs-4 text-dark lh-1'>ABUSAHIY GO</span>
      </div>
      {/* end::Logo */}

      {/* begin::Heading */}
      <div className='mb-10 text-center'>
        <h1 className='text-dark fw-bolder mb-2' style={{fontSize: '1.75rem'}}>
          Добро пожаловать!
        </h1>
        <div className='text-muted fs-6'>Введите номер телефона и пароль, чтобы войти.</div>
      </div>
      {/* end::Heading */}

      {formik.status && (
        <div className='mb-8 alert alert-danger py-3 border-0' style={{borderRadius: 12}}>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-6'>
        <label className='form-label fw-semibold text-gray-700 mb-2'>Номер телефона</label>
        <input
          placeholder='Введите номер телефона'
          {...formik.getFieldProps('phone_number')}
          className={clsx(
            'form-control form-control-lg bg-light border-0',
            {'is-invalid': formik.touched.phone_number && formik.errors.phone_number},
            {
              'is-valid': formik.touched.phone_number && !formik.errors.phone_number,
            }
          )}
          style={inputStyle}
          type='tel'
          name='phone_number'
          autoComplete='off'
        />
        {formik.touched.phone_number && formik.errors.phone_number && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.phone_number}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fw-semibold text-gray-700 mb-2'>Пароль</label>
        <div className='position-relative'>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder='Введите пароль'
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx('form-control form-control-lg bg-light border-0', {
              'border border-danger': formik.touched.password && formik.errors.password,
            })}
            style={{...inputStyle, paddingRight: '3rem'}}
          />
          <button
            type='button'
            className='btn btn-icon position-absolute top-50 end-0 translate-middle-y me-2'
            tabIndex={-1}
            onClick={() => setShowPassword((s) => !s)}
            title={showPassword ? 'Скрыть' : 'Показать'}
          >
            <KTIcon iconName={showPassword ? 'eye-slash' : 'eye'} className='fs-3 text-muted' />
          </button>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className='d-grid'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-lg text-white fw-bold border-0'
          style={{
            borderRadius: 12,
            background: 'linear-gradient(90deg, #003C9C 0%, #0059E9 55%, #1E9BFF 100%)',
          }}
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Войти</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Пожалуйста, подождите...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
    </form>
  )
}
